import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { calendarioService, CreateCalendarioData, UpdateCalendarioData, Calendario } from '@/services/calendario'
import { Calendar, Upload, Save, ArrowLeft } from 'lucide-react'

export function CalendarioFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    nome_calendario: '',
    descricao_calendario: '',
    periodo: 1,
    ano: new Date().getFullYear(),
    id_tipo_calendario: 1,
    vigente: 1
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const loadCalendario = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      const calendarioId = parseInt(id)
      if (isNaN(calendarioId)) {
        toast.error('ID do calendário inválido')
        navigate('/calendarios')
        return
      }
      
      const response = await calendarioService.getById(calendarioId)
      if (response.success && response.data) {
        const calendario = response.data[0] // A API retorna o objeto diretamente
        
        // Verificar se os campos essenciais existem
        if (!calendario.nome_calendario) {          
          toast.error('Nome do calendário não encontrado')
          navigate('/calendarios')
          return
        }
        
        setFormData({
          nome_calendario: calendario.nome_calendario,
          descricao_calendario: calendario.descricao_calendario || '',
          periodo: calendario.periodo || 1,
          ano: calendario.ano || new Date().getFullYear(),
          id_tipo_calendario: calendario.id_tipo_calendario || 1,
          vigente: calendario.vigente || 1
        })
      } else {
        console.error('Resposta da API não contém dados:', response)
        toast.error(response.error || 'Calendário não encontrado')
        navigate('/calendarios')
      }
    } catch (error) {
      console.error('Erro ao carregar calendário:', error)
      // Tratamento específico para erros do Docker/API
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        if (apiError.response?.status === 404) {
          toast.error('Calendário não encontrado')
        } else if (apiError.response?.status === 500) {
          toast.error('Erro interno do servidor')
        } else {
          toast.error(apiError.response?.data?.message || 'Erro ao carregar calendário')
        }
      } else if (error instanceof Error) {
        toast.error(`Erro ao carregar calendário: ${error.message}`)
      } else {
        toast.error('Erro ao carregar calendário')
      }
      navigate('/calendarios')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validar tipo de arquivo (PDF, DOC, DOCX)
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Apenas arquivos PDF, DOC ou DOCX são permitidos')
        return
      }
      
      // Validar tamanho (máximo 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo permitido: 50MB')
        return
      }
      
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formData.nome_calendario.trim()) {
      toast.error('Nome do calendário é obrigatório')
      return
    }

    if (!formData.descricao_calendario.trim()) {
      toast.error('Descrição é obrigatória')
      return
    }

    // Validação simplificada para os novos campos
    if (formData.id_tipo_calendario === undefined || formData.id_tipo_calendario === null ) {
      toast.error('Tipo de calendário é obrigatório')
      return
    }

    if (formData.vigente === undefined || formData.vigente === null) {
      toast.error('Status é obrigatório')
      return
    }

    if (!isEdit && !selectedFile) {
      toast.error('Arquivo é obrigatório para novos calendários')
      return
    }

    try {
      setSubmitting(true)

      if (isEdit) {
        // Atualizar calendário existente
        const updateData: UpdateCalendarioData = {  
          id_calendario: parseInt(id!),
          ...formData,
          id_tipo_calendario: parseInt(formData.id_tipo_calendario.toString())
        }

        const response = await calendarioService.update(updateData)
        
        if (response.success) {
          toast.success('Calendário atualizado com sucesso!')
          navigate('/calendarios')
        } else {
          toast.error(response.error || 'Erro ao atualizar calendário')
        }
      } else {
        // Criar novo calendário
        const createData: CreateCalendarioData = {
          ...formData,
          file: selectedFile!,
          id_tipo_calendario: parseInt(formData.id_tipo_calendario.toString())
        }

        const response = await calendarioService.create(createData)
        
        if (response.success) {
          toast.success('Calendário criado com sucesso!')
          navigate('/calendarios')
        } else {
          toast.error(response.error || 'Erro ao criar calendário')
        }
      }
    } catch (error) {
      console.error('Erro ao salvar:', error)
      // Tratamento específico para erros do Docker/API
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as any
        if (apiError.response?.status === 400) {
          toast.error('Dados inválidos para atualização')
        } else if (apiError.response?.status === 404) {
          toast.error('Calendário não encontrado para atualização')
        } else if (apiError.response?.status === 500) {
          toast.error('Erro interno do servidor')
        } else {
          toast.error(apiError.response?.data?.message || 'Erro ao salvar calendário')
        }
      } else if (error instanceof Error) {
        toast.error(`Erro ao salvar calendário: ${error.message}`)
      } else {
        toast.error('Erro ao salvar calendário')
      }
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {    
    if (isEdit && id) {
      loadCalendario()
    }
  }, [id])

  if (loading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Cabeçalho da página */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/calendarios')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
        
        <div className="flex items-center space-x-4">
          <Calendar className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? 'Editar Calendário' : 'Novo Calendário'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Atualizar informações do calendário acadêmico' : 'Adicionar novo calendário acadêmico'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nome do Calendário */}
            <div className="md:col-span-2">
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Calendário *
              </label>
              <input
                type="text"
                id="nome"
                value={formData.nome_calendario}
                onChange={(e) => handleInputChange('nome_calendario', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Calendário Acadêmico 2024"
                required
              />
            </div>

            {/* Ano */}
            <div>
              <label htmlFor="ano" className="block text-sm font-medium text-gray-700 mb-2">
                Ano *
              </label>
              <input
                type="number"
                id="ano"
                min="2020"
                max="2030"
                value={formData.ano}
                onChange={(e) => handleInputChange('ano', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Período */}
            <div>
              <label htmlFor="periodo" className="block text-sm font-medium text-gray-700 mb-2">
                Período *
              </label>
              <select
                id="periodo"
                value={formData.periodo}
                onChange={(e) => handleInputChange('periodo', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value={1}>1º Período</option>
                <option value={2}>2º Período</option>
                <option value={3}>3º Período</option>
                <option value={4}>4º Período</option>
              </select>
            </div>

            {/* Tipo de Calendário */}
            <div>
              <label htmlFor="tipo_calendario" className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Calendário *
              </label>
              <select
                id="tipo_calendario"
                value={formData.id_tipo_calendario}
                onChange={(e) => handleInputChange('id_tipo_calendario', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="1">Principal</option>
                <option value="2">Subcalendário</option>
              </select>
            </div>

            {/* Vigente */}
            <div>
              <label htmlFor="vigente" className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                id="vigente"
                value={formData.vigente}
                onChange={(e) => handleInputChange('vigente', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value={1}>Vigente</option>
                <option value={2}>Não Vigente</option>
              </select>
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                id="descricao"
                rows={4}
                value={formData.descricao_calendario}
                onChange={(e) => handleInputChange('descricao_calendario', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Descrição detalhada do calendário acadêmico"
                required
              />
            </div>

            {/* Upload de Arquivo */}
            {!isEdit && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo do Calendário *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Selecionar arquivo</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">ou arraste e solte</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX até 50MB
                    </p>
                    {selectedFile && (
                      <p className="text-sm text-green-600 font-medium">
                        Arquivo selecionado: {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/calendarios')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Atualizar' : 'Salvar'} Calendário
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
